import { Link } from 'react-router-dom';

/**
 * Footer - Public layout footer component
 * 
 * CRITICAL: Uses React Router 5 <Link> component
 * 
 * Features:
 * - 4-column grid layout (responsive)
 * - Brand, Kurumsal, Hukuki, İletişim columns
 * - Internal links with React Router Link
 * - Copyright bar at bottom
 * - ContractCode green branding
 * 
 * @returns {JSX.Element} Footer component
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          
          {/* Column 1: Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src="/contractcode-logo.png"
                alt="ContractCode"
                className="h-20 w-auto"
              />
            </Link>
            <p className="text-lg text-gray-600 leading-relaxed">
              contractcode, profesyonel hukuki belge ihtiyaçlarınız için akıllı form sihirbazı sunan bir platformdur.
            </p>
          </div>

          {/* Column 2: Şablonlar */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-4">Şablonlar</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/publictemplates"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Sözleşmeler
                </Link>
              </li>
              <li>
                <Link
                  to="/publictemplates"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Anlaşmalar
                </Link>
              </li>
              <li>
                <Link
                  to="/publictemplates"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Formlar
                </Link>
              </li>
              <li>
                <Link
                  to="/publictemplates"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Kurumsal Belgeler
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Şirket */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-4">Şirket</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Hakkımızda
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Fiyatlandırma
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  İletişim
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Yasal */}
          <div>
            <h3 className="text-gray-900 font-semibold text-lg mb-4">Yasal</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Kullanım Koşulları
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Gizlilik Politikası
                </Link>
              </li>
              <li>
                <Link
                  to="/kvkk"
                  className="text-lg text-gray-600 hover:text-primary-500 transition-colors duration-200"
                >
                  Çerez Politikası
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Copyright */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-lg text-gray-600">
              © {currentYear} contractcode. Tüm hakları saklıdır.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://twitter.com/contractcode"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-primary-500 transition-colors duration-200"
                aria-label="Twitter"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a
                href="https://linkedin.com/company/contractcode"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-primary-500 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

